import { Button, Divider, Flex, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCheckbox } from "@tabler/icons-react";
import { useState } from "react";
import { withPolymorphicClickAnalytics } from "@/analytics/hoc/withPolymorphicClickAnalytics";
import { useProductOfferStore } from "@/sales-tools";
import { HomeProductFlow } from "@/sales-tools/home-product/components/HomeProductFlow";
import { EligibilityStatus } from "@/sales-tools/home-product/types";
import { ShieldXIcon } from "@/sales-tools/icons/ShieldXIcon";
import { PlanCard } from "@/sales-tools/unified-sales-view/components/PlanCard";
import { SaleTracker } from "@/sales-tools/unified-sales-view/components/SaleTracker";
import { usePartner, useSessionId } from "@/sdk";
import { SaleTrackingModal } from "../multi-product/components";

export function UnifiedSalesView({ isChat = false }: { isChat?: boolean }) {
    const partner = usePartner();
    const sessionId = useSessionId();
    const { getProducts, updateSelectedProduct, getSelectedProduct, getProductInfo } = useProductOfferStore();
    const products = getProducts(sessionId);
    const selectedProduct = getSelectedProduct(sessionId);
    const [opened, { open, close }] = useDisclosure(false);

    const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "ButtonClick");
    const [notOffered, setNotOffered] = useState(false);
    const showNoOfferButton = !selectedProduct && ["youtubetv"].includes(partner);

    const eligibleProducts = products.filter(
        (p) => !!getProductInfo(sessionId, p.productSku) && p.status === EligibilityStatus.Eligible,
    );
    const shouldUseGenericSaleTracker = isChat || partner === "mcafee";

    return (
        <>
            <Flex h="100%" style={{ overflow: "auto" }} direction="column" mt="sm">
                <Text ml="xxs" size="sm" fw="var(--mantine-font-weight-heavy)" c="dark.0">
                    {isChat ? "Available plans" : "Potential upgrades"}
                </Text>
                <Divider mt="md" mb="md" color="primary.5" />

                {selectedProduct?.isPrimaryProduct ? (
                    <HomeProductFlow onPrimarySaleComplete={() => updateSelectedProduct(sessionId, undefined)} />
                ) : products.length > 0 ? (
                    <Stack gap="0.75rem" p="sm">
                        {eligibleProducts.length > 0 ? (
                            eligibleProducts.map((p, index) => (
                                <PlanCard
                                    saleProgress={p.saleProgress}
                                    onClick={() => {
                                        updateSelectedProduct(sessionId, p);
                                        if (!p.isPrimaryProduct) {
                                            open();
                                        }
                                    }}
                                    position={index}
                                    key={p.productSku}
                                    product={getProductInfo(sessionId, p.productSku)!}
                                    partner={partner}
                                    variant={isChat ? "full" : "compact"}
                                    showEligibleTag={isChat}
                                />
                            ))
                        ) : (
                            <Flex mt="md">
                                <ShieldXIcon />
                                <Text ml="3xs" size="xs" c="dark.2">
                                    Not eligible for additional plans
                                </Text>
                            </Flex>
                        )}
                    </Stack>
                ) : (
                    <Flex mt="md" p="sm">
                        <ShieldXIcon />
                        <Text ml="3xs" size="xs" c="dark.2">
                            Not eligible for any upgrades
                        </Text>
                    </Flex>
                )}
            </Flex>
            {selectedProduct &&
                (shouldUseGenericSaleTracker ? (
                    <SaleTracker
                        productInfo={getProductInfo(sessionId, selectedProduct.productSku)}
                        selectedProduct={selectedProduct}
                        opened={opened}
                        onClose={close}
                    />
                ) : (
                    <SaleTrackingModal
                        productInfo={getProductInfo(sessionId, selectedProduct.productSku)}
                        selectedProduct={selectedProduct}
                        opened={opened}
                        onClose={close}
                    />
                ))}
            {showNoOfferButton && (
                <Flex bg="dark.4" h="4rem" mih="4rem" mah="4rem" align="center" justify="flex-end" p="md" mt="auto">
                    {notOffered ? (
                        <Flex align="center" gap="xxs" data-testid="no-offer-text">
                            <IconCheckbox size={16} />
                            <Text fz="xs" c="dark.1">
                                Got it—no offer made
                            </Text>
                        </Flex>
                    ) : (
                        <Flex align="center" gap="xxs">
                            <AnalyticsButton
                                analytics={{
                                    actionId: "NoOfferButton",
                                }}
                                color="dark.1"
                                bd="dark.1"
                                fz="xs"
                                variant="transparent"
                                data-testid="no-offer-button"
                                size="xs"
                                onClick={() => {
                                    setNotOffered(true);
                                }}
                            >
                                <u>I didn’t make an offer</u>
                            </AnalyticsButton>
                        </Flex>
                    )}
                </Flex>
            )}
        </>
    );
}
